import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

// Seconds → compact clock string. < 1h reads "m:ss", ≥ 1h reads "h:mm:ss".
// Used for audio/video track lengths. Returns '—' when the duration is unknown
// (0, NaN, or not yet loaded).
export function formatDuration(seconds: number | undefined | null): string {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return '—'
  const total = Math.round(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

// Relative "last seen/synced" formatting. Each magnitude rolls up into the
// next unit ("26h" → "1d 2h", "9d" → "1w 2d") so stale screens read at a
// glance instead of accumulating unbounded hour counts.
export function formatRelativeSyncTime(dateStr: string | undefined): string {
  if (!dateStr) return '—'
  // Follows the page language (applyLanguage sets <html lang>), so the Arabic
  // UI stops showing "just now" / "5m ago" in English. Compact units both ways.
  const ar = typeof document !== 'undefined' && document.documentElement.lang?.startsWith('ar')
  const U = ar
    ? { now: 'الآن', ago: (x: string) => `منذ ${x}`, m: 'د', h: 'س', d: 'ي', w: 'أسبوع' }
    : { now: 'just now', ago: (x: string) => `${x} ago`, m: 'm', h: 'h', d: 'd', w: 'w' }
  const unit = (n: number, u: string) => (ar ? `${n} ${u}` : `${n}${u}`)
  const diffMs = Date.now() - new Date(dateStr).getTime()
  if (diffMs < 60_000) return U.now
  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 60) return U.ago(unit(diffMin, U.m))
  const h = Math.floor(diffMin / 60)
  if (h < 24) {
    const m = diffMin % 60
    return U.ago(m === 0 ? unit(h, U.h) : `${unit(h, U.h)} ${unit(m, U.m)}`)
  }
  const d = Math.floor(h / 24)
  if (d < 7) {
    const hr = h % 24
    return U.ago(hr === 0 ? unit(d, U.d) : `${unit(d, U.d)} ${unit(hr, U.h)}`)
  }
  const w = Math.floor(d / 7)
  const rd = d % 7
  return U.ago(rd === 0 ? unit(w, U.w) : `${unit(w, U.w)} ${unit(rd, U.d)}`)
}

export function formatDate(dateStr: string, locale = 'en'): string {
  return new Date(dateStr).toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-GB', {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

// Clock time (hour:minute) in the locale's convention — 24h for en-GB, 12h with
// Arabic AM/PM for ar-SA. Pairs with formatDate for "date · time" timestamps.
export function formatTime(dateStr: string, locale = 'en'): string {
  return new Date(dateStr).toLocaleTimeString(locale === 'ar' ? 'ar-SA' : 'en-GB', {
    hour: '2-digit', minute: '2-digit',
  })
}

export function statusColor(status: string): string {
  switch (status) {
    case 'online':  return 'text-green-400'
    case 'offline': return 'text-red-400'
    case 'idle':    return 'text-amber-400'
    default:        return 'text-muted-foreground'
  }
}

export function statusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'online':  return 'default'
    case 'offline': return 'destructive'
    case 'idle':    return 'secondary'
    default:        return 'outline'
  }
}
